import { useState, createContext } from 'react'
import { toast } from 'react-toastify'
import { marketStockService } from '@/services/marketStock'
import { useTableData } from '@/hooks/useTableData'
import { useGlobalInformations } from '@/hooks/useGlobalInformations'
import { usePrecification } from '@/hooks/usePrecification'
import { getParamByParam } from '../utils'
import { ETipoAvaliacao } from '@/types/enums/inspections'

interface IGraphys {
  children: React.ReactNode
}

type GraphysDataContextInterface = {
  internalData: any
  kbbData: any
  fipeData: any
  stockGraphyData: any
  graphyReferenceHistory: GraphyPeriodType | null
  getInternal: (params?: any) => Promise<void>
  getKBB: (params?: any) => Promise<void>
  getFipeData: (params?: any) => Promise<void>
  getStockData: (params?: any) => Promise<void>
  getReferenceHistoryPrecification: (params?: any) => Promise<void>
  clearReferenceHistoryPrecification: () => void
}

type GraphyData = {
  meses: number
  percentualMedio: number | null
  valor: number | null
  quantidadeAmostra: number
}

type GraphyPeriodType = {
  ultimosTresMeses: GraphyData
  ultimosSeisMeses: GraphyData
}

export const GraphysData = createContext({} as GraphysDataContextInterface)

export function GraphysDataProvider({ children }: IGraphys) {
  const [stockGraphyData, setStockGraphyData] = useState([])
  const [fipeData, setFipeData] = useState(null)
  const [kbbData, setKbbData] = useState(null)
  const [internalData, setInternalData] = useState([])
  const [graphyReferenceHistory, setGraphyReferenceHistory] =
    useState<GraphyPeriodType | null>(null)

  const { ROW_DATA } = useTableData()
  const { DIRECTORSHIP_LIST } = useGlobalInformations()
  const { VEHICLE_INSPECTION } = usePrecification()

  async function getReferenceHistoryPrecification(params: any) {
    try {
      const res = await marketStockService.getReferenceHistory(params)
      setGraphyReferenceHistory(res?.data?.data ?? null)
    } catch (e: any) {
      setGraphyReferenceHistory(null)
      toast.error(e.message)
    }
  }

  function clearReferenceHistoryPrecification() {
    setGraphyReferenceHistory(null)
  }

  async function getStockData(v?: any) {
    const params = {
      uf: v?.uf ?? '',
      city: v?.city ?? '',
      modelId: ROW_DATA?.hiddenModelId,
      yearModel: ROW_DATA?.yearModel
    }

    try {
      const res = await marketStockService.getStock(params)
      setStockGraphyData(res.data.data ?? [])
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function getFipeData({ quantidadeMeses }: { quantidadeMeses: number }) {
    const params = {
      codigoFipe: ROW_DATA?.hiddenFipe,
      ano: ROW_DATA?.yearModel
    }

    if (Object.values(params).indexOf('--') > -1) {
      toast.error('Não há dados da fipe.')
      return
    }

    try {
      const res = await marketStockService.fipeModel({
        ...params,
        quantidadeMeses
      })
      const result = res?.data?.data

      if (result) setFipeData(result)
      else {
        setFipeData(null)
        toast.warning('Fipe não encontrada')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function getKBB() {
    const params = {
      fipe: ROW_DATA?.hiddenFipe,
      yModel: ROW_DATA?.yearModel,
      inspectionId: ROW_DATA?.id!
    }

    if (Object.values(params).indexOf('--') > -1) {
      setKbbData(null)
      toast.error('Preço KBB não encontrado')
      return
    }

    try {
      const res = await marketStockService.kbbPrice(
        params.fipe,
        params.yModel,
        params.inspectionId
      )
      const result = res?.data?.data

      if (result) setKbbData(result)
      else {
        setKbbData(null)
        toast.warning('Preço KBB não encontrado')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  async function getInternal(filters?: any) {
    if (!VEHICLE_INSPECTION) return
    const params = {
      modeloId: VEHICLE_INSPECTION?.modeloId,
      anoModelo: VEHICLE_INSPECTION?.anoModelo,
      tipoAvaliacao: VEHICLE_INSPECTION?.tipoAvaliacaoLegado,
      diretoriaId: filters?.directory
        ? getParamByParam(
            filters.directory,
            DIRECTORSHIP_LIST,
            'descricao',
            'id'
          )
        : '',
      faixaKmInicial: filters?.minKm ?? '',
      faixaKmFinal: filters?.maxKm ?? ''
    }

    if (Object.values(params).includes('--')) {
      toast.error(
        'Histórico de precificação interna não retornou nenhum registro'
      )
      return
    }

    try {
      const res = await marketStockService.internal(params)
      const result = res?.data?.data

      if (result.length) {
        setInternalData(result)
      } else {
        if (params.tipoAvaliacao === ETipoAvaliacao.VistoriaCompra) {
          toast.warning(
            'Histórico de precificação interna não retornou nenhum registro'
          )
        }

        setInternalData([])
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <GraphysData.Provider
      value={{
        stockGraphyData,
        fipeData,
        kbbData,
        internalData,
        graphyReferenceHistory,
        getStockData,
        getFipeData,
        getKBB,
        getInternal,
        getReferenceHistoryPrecification,
        clearReferenceHistoryPrecification
      }}
    >
      {children}
    </GraphysData.Provider>
  )
}
